import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import config from '../config';

const { apiBaseURL } = config;

const formatValue = (value) => {
  if (value >= 10000) return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const IndexCard = ({ index }) => {
  const isPositive = index.dailyChangePercent >= 0;
  const changeClass = isPositive ? 'index-change positive' : 'index-change negative';
  const changePrefix = isPositive ? '+' : '';

  const chartData = index.prices.map(p => ({
    date: formatDate(p.date),
    close: p.close,
  }));

  const minClose = Math.min(...index.prices.map(p => p.close));
  const maxClose = Math.max(...index.prices.map(p => p.close));
  const padding = (maxClose - minClose) * 0.05 || 1;

  return (
    <div className="index-card">
      <div className="index-card-header">
        <span className="index-name">{index.name}</span>
        <span className={changeClass}>
          {changePrefix}{index.dailyChangePercent.toFixed(2)}% <span className="index-period">today</span>
        </span>
      </div>
      <div className="index-current-value">{formatValue(index.current)}</div>
      <div className="index-chart-container">
        <ResponsiveContainer width="100%" height={90}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
            <XAxis dataKey="date" hide />
            <YAxis domain={[minClose - padding, maxClose + padding]} hide />
            <Tooltip
              formatter={(value) => [formatValue(value), 'Close']}
              labelFormatter={(label) => label}
              contentStyle={{ fontSize: '12px', padding: '4px 8px' }}
            />
            <Line
              type="monotone"
              dataKey="close"
              stroke={isPositive ? '#27ae60' : '#e74c3c'}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="index-chart-label">Last 30 days</div>
    </div>
  );
};

const IndexCharts = () => {
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIndexPrices = async () => {
      try {
        const response = await fetch(`${apiBaseURL}/api/index-prices`);
        if (!response.ok) throw new Error('Failed to fetch index data');
        const data = await response.json();
        setIndices(data.indices || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIndexPrices();
  }, []);

  if (loading) {
    return (
      <div className="index-charts-container loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || indices.length === 0) {
    return null;
  }

  return (
    <div className="index-charts-container">
      {indices.map((index) => (
        <IndexCard key={index.symbol} index={index} />
      ))}
    </div>
  );
};

export default IndexCharts;
